import * as React from 'react';
import './UsersManagement.css';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
    DataGrid,
    GridColDef,
    GridRowModel,
    GridActionsCellItem,
    GridToolbarContainer,
    GridRowModes,
    GridRowModesModel,
    GridFilterModel,
    GridRenderEditCellParams,
} from '@mui/x-data-grid';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { fetchUsers, createUser, updateUser, deleteUser } from '../api/users';

/* Types */
interface User {
    id: string; // DataGrid requires 'id'
    _id?: string; // Backend ID
    username: string;
    password?: string; // Only for sending updates
    notes?: string;
    isNew?: boolean;
}

/* Toolbar Component */
interface EditToolbarProps {
    setRows: React.Dispatch<React.SetStateAction<GridRowModel[]>>;
    setRowModesModel: React.Dispatch<React.SetStateAction<GridRowModesModel>>;
    searchText: string;
    setSearchText: (value: string) => void;
}

function EditToolbar(props: EditToolbarProps) {
    const { setRows, setRowModesModel, searchText, setSearchText } = props;

    const handleClick = () => {
        const id = `temp-${Math.random().toString(36).substr(2, 9)}`;
        setRows((oldRows) => {
            // Remove any previous unsaved new rows to avoid duplicates/empty rows
            const cleanRows = oldRows.filter(r => !r.isNew);
            return [
                ...cleanRows,
                { id, username: '', notes: '', isNew: true },
            ];
        });
        // Clear other edit modes and focus on the new row
        setRowModesModel({
            [id]: { mode: GridRowModes.Edit, fieldToFocus: 'username' },
        });
    };

    return (
        <GridToolbarContainer sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                    User Management
                </Typography>
                <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={handleClick}>
                    Add User
                </Button>
            </Box>
            <TextField
                variant="outlined"
                size="small"
                placeholder="Search users..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{ width: 300 }}
            />
        </GridToolbarContainer>
    );
}

const UsersManagement: React.FC = () => {
    const [rows, setRows] = React.useState<User[]>([]);
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
    const [toast, setToast] = React.useState<{ message: string; severity: 'success' | 'error' } | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [searchText, setSearchText] = React.useState('');

    React.useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const data = await fetchUsers();
            // Map _id to id for DataGrid
            const mappedUsers = data.map((u: any) => ({ ...u, id: u._id }));
            setRows(mappedUsers);
        } catch (err) {
            setToast({ message: 'Failed to load users', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleRowEditStop = (params: any, event: any) => {
        if (params.reason === 'rowFocusOut') {
            event.defaultMuiPrevented = true;
        }
    };

    const handleEditClick = (id: string) => () => {
        setRowModesModel((oldModel) => {
            const newModel = { ...oldModel };
            // Explicitly set all currently edited rows to View mode to cancel them
            Object.keys(newModel).forEach((key) => {
                newModel[key] = { mode: GridRowModes.View, ignoreModifications: true };
            });
            // Set the new row to Edit mode
            newModel[id] = { mode: GridRowModes.Edit };
            return newModel;
        });
    };

    const handleSaveClick = (id: string) => () => {
        // Saving keeps the mode as view
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const handleDeleteClick = (id: string) => async () => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;

        const row = rows.find((r) => r.id === id);
        if (!row) return;

        if (row.isNew) {
            setRows(rows.filter((row) => row.id !== id));
            return;
        }

        try {
            await deleteUser(row._id || id);
            setRows(rows.filter((row) => row.id !== id));
            setToast({ message: 'User deleted successfully', severity: 'success' });
        } catch (err) {
            setToast({ message: 'Failed to delete user', severity: 'error' });
        }
    };

    const handleCancelClick = (id: string) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });

        const row = rows.find((r) => r.id === id);
        if (row!.isNew) {
            setRows(rows.filter((r) => r.id !== id));
        }
    };

    const processRowUpdate = async (newRow: GridRowModel) => {
        const userRow = newRow as User;

        try {
            if (userRow.isNew) {
                // Create
                if (!userRow.username || !userRow.password) {
                    throw new Error("Username and Password are required for new users.");
                }
                const created = await createUser({
                    username: userRow.username,
                    password: userRow.password,
                    notes: userRow.notes
                });
                const savedRow = { ...created.user, id: created.user._id, isNew: false };
                setRows(rows.map((row) => (row.id === newRow.id ? savedRow : row)));
                setToast({ message: 'User created successfully', severity: 'success' });
                return savedRow;
            } else {
                // Update
                const payload: any = {
                    username: userRow.username,
                    notes: userRow.notes
                };
                // Only send password if it was somehow changed
                if (userRow.password && userRow.password.trim() !== "") {
                    payload.password = userRow.password;
                }

                await updateUser(userRow._id!, payload);
                const updatedRow = { ...userRow, isNew: false, password: undefined }; // Don't keep password in state
                setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
                setToast({ message: 'User updated successfully', severity: 'success' });
                return updatedRow;
            }
        } catch (err: any) {
            setToast({ message: err.response?.data?.error || err.message || 'Operation failed', severity: 'error' });
            throw err;
        }
    };

    const handleProcessRowUpdateError = (error: any) => {
        console.error(error);
    };

    // Password Edit Cell
    const PasswordEditInput = (params: GridRenderEditCellParams) => {
        const { id, value, field, api } = params;
        const [showPassword, setShowPassword] = React.useState(false);

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            api.setEditCellValue({ id, field, value: e.target.value });
        };

        const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
        };

        return (
            <TextField
                type={showPassword ? 'text' : 'password'}
                value={value || ''}
                onChange={handleChange}
                fullWidth
                placeholder={params.row.isNew ? "Required" : "Blank to keep"}
                inputProps={{
                    autoComplete: 'new-password',
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => setShowPassword((show) => !show)}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                            >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    )
                }}
            />
        );
    };

    // Password View Cell
    const PasswordView = () => <span>********</span>;


    const UsernameEditInput = (params: GridRenderEditCellParams) => {
        const { id, value, field, api } = params;
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            api.setEditCellValue({ id, field, value: e.target.value });
        };
        return <TextField
            value={value || ''}
            onChange={handleChange}
            fullWidth
            placeholder="Required"
            inputProps={{ autoComplete: 'off' }}
        />;
    };

    const columns: GridColDef[] = [
        {
            field: 'srNo',
            headerName: 'Sr. No.',
            width: 70,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const index = params.api.getRowIndexRelativeToVisibleRows(params.id);
                // Check if index is a number (it can be undefined during state updates)
                if (typeof index === 'number') {
                    return index + 1;
                }
                // Fallback using the current rows state
                const fallbackIndex = rows.findIndex((r) => r.id === params.id);
                return fallbackIndex >= 0 ? fallbackIndex + 1 : '-';
            },
        },
        {
            field: 'username',
            headerName: 'Username',
            width: 200,
            editable: true,
            renderEditCell: (params) => <UsernameEditInput {...params} />,
            renderHeader: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Username
                    <Typography color="error" sx={{ ml: 0.5 }}>*</Typography>
                </Box>
            ),
        },
        {
            field: 'password',
            headerName: 'Password',
            width: 200,
            editable: true,
            renderCell: PasswordView,
            renderEditCell: (params) => <PasswordEditInput {...params} />,
            renderHeader: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Password
                    <Typography color="error" sx={{ ml: 0.5 }}>*</Typography>
                </Box>
            ),
        },
        { field: 'notes', headerName: 'Notes', flex: 1, editable: true },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SaveIcon />}
                            label="Save"
                            sx={{ color: 'primary.main' }}
                            onClick={handleSaveClick(id as string)}
                        />,
                        <GridActionsCellItem
                            icon={<CloseIcon />}
                            label="Cancel"
                            className="textPrimary"
                            onClick={handleCancelClick(id as string)}
                            color="inherit"
                        />,
                    ];
                }

                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        onClick={handleEditClick(id as string)}
                        color="primary"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={handleDeleteClick(id as string)}
                        color="error"
                    />,
                ];
            },
        },
    ];

    const filterModel: GridFilterModel = React.useMemo(
        () => ({
            items: [],
            quickFilterValues: searchText ? [searchText] : undefined,
        }),
        [searchText]
    );

    return (
        <Box sx={{ width: '100%', height: 600, '& .actions': { color: 'text.secondary' }, '& .textPrimary': { color: 'text.primary' } }}>
            <DataGrid
                rows={rows}
                columns={columns}
                editMode="row"
                rowModesModel={rowModesModel}
                onRowModesModelChange={setRowModesModel}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={handleProcessRowUpdateError}
                filterModel={filterModel}
                // @ts-ignore
                slots={{
                    toolbar: EditToolbar,
                }}
                slotProps={{
                    toolbar: { setRows, setRowModesModel, searchText, setSearchText },
                }}
                loading={loading}
            />
            <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)}>
                <Alert onClose={() => setToast(null)} severity={toast?.severity} sx={{ width: '100%' }}>
                    {toast?.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default UsersManagement;
